import { Box, Button, HStack } from '@chakra-ui/react'
import AccessAccountButton from 'components/buttons/AccessAccountButton'
import ScheduleConsultationButton from 'components/buttons/ScheduleConsultationButton'
import WhatsappButton from 'components/buttons/WhatsappButton'
import CuideiLogo from 'components/modules/cuidei/CuideiLogo'
import { psychologyProblems } from 'components/sections/PsychologyIntroSection/components/PsychologyProblemsGrid'
import MedleveLogo from 'components/svg/MedleveLogo'
import OmensLogoLink from 'components/svg/OmensLogoLink'
import { Label } from 'components/typography'
import { CUIDEI_ROUTES } from 'constants/cuidei'
import useIntersectionObserver from 'hooks/helpers/useIntersectionObserver'
import { useIsCuideiPage } from 'hooks/helpers/useIsCuideiPage'
import NavbarMenu from 'layouts/LadingLayout/components/Navbar/components/NavbarDesktop/NavbarMenu'
import NavbarLinkContainer from 'layouts/LadingLayout/components/Navbar/components/NavbarLinkContainer'
import { useNavbarHelpers } from 'layouts/LadingLayout/components/Navbar/hooks/useNavbarHelpers'
import {
  navbarCourses,
  navbarDermatologyPathologies,
  navbarDermatologyPathologiesSmartfit,
  navbarPathologies,
  navbarPathologiesSmartfit,
  navbarProducts,
  navbarSpecialists
} from 'layouts/LadingLayout/components/Navbar/navbarLinks'
import { capitalize } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useEffect, useMemo, useState } from 'react'
import { IoChatbubbleOutline } from 'react-icons/io5'
import { useAppSelector } from 'redux/store'
import { getCookies, isSmartfitClient } from 'utils/cookieHelpers'

export const NAVBAR_HEIGHT = 72
export const NAVBAR_HEIGHT_IN_PX = `${NAVBAR_HEIGHT}px`
export const HERO_SECTION_ID = 'hero-section'

const NavbarDesktop: FC = () => {
  const profile = useAppSelector((state) => state.auth.profile)
  const [heroSection, setHeroSection] = useState<Element | null>(null)
  const entry = useIntersectionObserver(heroSection)
  const { isProducts, isSexualHealth, isDermatology, isPsychology, isWeight } = useNavbarHelpers()
  const isCuidei = useIsCuideiPage()
  const { pathname } = useRouter()
  const [isOmensFit, setIsOmensFit] = useState<boolean>(false)
  const [isSmartfit, setIsSmartfit] = useState<boolean>(false)
  const [isMedleve, setIsMedleve] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const isSolidNavbar = useMemo(() => {
    if (!heroSection) return true

    return !entry?.isIntersecting
  }, [entry?.isIntersecting, heroSection])

  useEffect(() => {
    const checkCookies = () => {
      const isWeightAddressValid = getCookies('isWeightAddress')?.isValid
      const isSmartfitValid = isSmartfitClient()
      const isMedleveValid = getCookies('isMedleveAddress')?.isValid

      setIsOmensFit(isWeightAddressValid)
      setIsSmartfit(isSmartfitValid)
      setIsMedleve(isMedleveValid)

      setLoading(false)
    }
    const heroSectionElement = document.getElementById(HERO_SECTION_ID)

    if (heroSectionElement) setHeroSection(heroSectionElement)
    checkCookies()
  }, [])

  const actualFirstName = useMemo(() => {
    if (!profile?.firstName) return ''

    return capitalize(profile.firstName.split(' ')[0])
  }, [profile?.firstName])

  if (loading) return null

  return (
    <HStack
      bg={isSolidNavbar ? 'grayscale.20' : isProducts ? 'rgba(246, 244, 232, 0.1)' : 'transparent'}
      minH={NAVBAR_HEIGHT_IN_PX}
      zIndex="100"
      width="100%"
      shadow="sm"
      justifyContent="space-between"
      transition="background-color 250ms ease-out"
      pos="absolute"
      {...(!heroSection && {
        position: 'sticky',
        top: 0
      })}
    >
      <Box
        bg={isSolidNavbar ? 'grayscale.20' : 'transparent'}
        backdropFilter="blur(30px)"
        w="full"
        h="full"
        position="absolute"
        zIndex="-1"
      />
      <Box w="900px">
        <HStack gap="0.5rem" justifyContent="space-between" overflow="hidden" w="full" pl="24px">
          {isCuidei ? (
            <Link href={CUIDEI_ROUTES.landing}>
              <Box py="16px" pl="8px">
                <CuideiLogo size="lg" />
              </Box>
            </Link>
          ) : isMedleve ? (
            <Link href="/">
              <MedleveLogo h={'80px'} w={'auto'} color={isSolidNavbar ? 'medleve' : 'alabaster'} />
            </Link>
          ) : (
            <OmensLogoLink color={isSolidNavbar ? 'darkchoc' : 'alabaster'} />
          )}
          {!isOmensFit && !isMedleve && !isCuidei && (
            <>
              <NavbarMenu
                isActive={isSexualHealth}
                title="Saúde sexual"
                href={isSmartfit ? '/consultar' : '/saude-sexual'}
                items={isSmartfit ? navbarPathologiesSmartfit : navbarPathologies}
                solidNavbar={isSolidNavbar}
                showCourses
                showProducts
              />
              <NavbarMenu
                isActive={isDermatology}
                title="Pele & Cabelo"
                href={isSmartfit ? '/consultar' : '/dermatologia'}
                items={isSmartfit ? navbarDermatologyPathologiesSmartfit : navbarDermatologyPathologies}
                solidNavbar={isSolidNavbar}
              />
              <NavbarLinkContainer>
                <Link href="/emagrecimento">
                  <Label color={isSolidNavbar ? 'darkchoc' : 'alabaster'}>Perda de Peso</Label>
                </Link>
              </NavbarLinkContainer>
              <NavbarMenu
                // isActive={}
                title="Produtos"
                // href="/produtos"
                items={navbarProducts}
                solidNavbar={isSolidNavbar}
              />
              {!isSmartfit && (
                <NavbarMenu
                  isActive={isPsychology}
                  title="Psicologia"
                  href="/psicologia-sexual-parceria"
                  items={psychologyProblems.map(({ href, label }) => ({ href, label }))}
                  solidNavbar={isSolidNavbar}
                />
              )}
              {!isSmartfit && (
                <NavbarMenu
                  solidNavbar={isSolidNavbar}
                  title="Profissionais"
                  href="/profissionais"
                  items={navbarSpecialists}
                />
              )}
              {isSmartfit && <NavbarMenu solidNavbar={isSolidNavbar} title="Cursos" items={navbarCourses} />}
              <NavbarLinkContainer>
                <Link href="https://omens.com.br/blog" target="_blank" rel="noopener noreferrer">
                  <Label color={isSolidNavbar ? 'darkchoc' : 'alabaster'}>Blog</Label>
                </Link>
              </NavbarLinkContainer>
              <NavbarLinkContainer>
                <Link href="/faq">
                  <Label color={isSolidNavbar ? 'darkchoc' : 'alabaster'}>Dúvidas</Label>
                </Link>
              </NavbarLinkContainer>
            </>
          )}
        </HStack>
      </Box>

      <HStack gap="14px" pl="12px" pr="24px">
        {pathname === '/plizmarmi' || isMedleve ? (
          <></>
        ) : isOmensFit || pathname === '/emagrecimento' || pathname === '/omensmaisfit' ? (
          <ScheduleConsultationButton title="Começar sua jornada" />
        ) : (
          <ScheduleConsultationButton />
        )}
        {isMedleve && (
          <Button
            as="a"
            href="https://chat.whatsapp.com/DYNysh1leF2HL0aXAPSmU2?s=cl&p=i&mlu=4&ilr=4"
            target="_blank"
            rel="noopener noreferrer"
            leftIcon={<IoChatbubbleOutline />}
            bg="#25D366"
            color="white"
            size="sm"
            borderRadius="full"
            _hover={{ bg: '#1ebe5d' }}
          >
            Comunidade
          </Button>
        )}
        <AccessAccountButton
          btnBg={isSolidNavbar ? 'alabaster' : 'rgba(23, 23, 18, 0.1)'}
          iconColor={isSolidNavbar ? 'darkchoc' : 'alabaster'}
        />
        <WhatsappButton
          iconSize="20px"
          btnBg={isSolidNavbar ? 'alabaster' : 'rgba(23, 23, 18, 0.1)'}
          iconColor={isSolidNavbar ? 'darkchoc' : 'alabaster'}
        />
        <Box>
          {actualFirstName && (
            <Link prefetch={false} href="/me">
              <NavbarLinkContainer solidNavbar={isSolidNavbar}>
                <Label>{actualFirstName}</Label>
              </NavbarLinkContainer>
            </Link>
          )}
        </Box>
      </HStack>
    </HStack>
  )
}

export default NavbarDesktop
